import { computed, type ComputedRef } from 'vue'
import { useStore } from 'vuex'
import { DateTime } from 'luxon'
import {
  SET_SWIFTAID_GIVING_PLAN,
  SET_DONOR_PLAN_STATUS_KNOWN,
} from '@/store/mutation-types'
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import type {
  SwiftaidGivingPlan,
  DonorPlanSubscription,
} from '@/types/swiftaidGiving'

export type UseDonorPlanReturn = {
  plan: ComputedRef<SwiftaidGivingPlan>
  /** True once GET /me/plan has settled (or forced mock paid). */
  isPlanKnown: ComputedRef<boolean>
  isPaid: ComputedRef<boolean>
  canDownloadStatement: ComputedRef<boolean>
  canViewStatementHistory: ComputedRef<boolean>
  canViewGivingRecord: ComputedRef<boolean>
  canAddManualDonation: ComputedRef<boolean>
  /** Subscription renewal date formatted as "d MMMM yyyy", or null when unknown. */
  renewsAt: ComputedRef<string | null>
  /** Date paid access lapses formatted as "d MMMM yyyy", or null when the subscription is not ending. */
  endsAt: ComputedRef<string | null>
  /** Subscription price formatted as a GBP string (e.g. "£19.99"), or null when unknown. */
  subscriptionPrice: ComputedRef<string | null>
  /** Subscription billing interval (e.g. "month", "year"), or null when unknown. */
  subscriptionInterval: ComputedRef<string | null>
  setPlan: (plan: SwiftaidGivingPlan) => void
}

/**
 * Donor plan state for free vs Swiftaid Giving (paid) dashboard experiences.
 * Plan is loaded from GET /me/plan (mock or API); VITE_MOCK_SWIFTAID_GIVING_PAID forces paid in dev.
 */
export function useDonorPlan(): UseDonorPlanReturn {
  const store = useStore()

  const mockPaid = import.meta.env.VITE_MOCK_SWIFTAID_GIVING_PAID === 'true'

  const plan = computed(
    () => (store.state.donor.swiftaidGivingPlan as SwiftaidGivingPlan) ?? 'free'
  )

  // Until the plan lookup settles, treat entitlements as unknown — never as free.
  const isPlanKnown = computed(
    () => mockPaid || Boolean(store.getters.donorPlanStatusKnown)
  )

  const isPaid = computed(() => mockPaid || plan.value === 'paid')

  const subscription = computed(
    () =>
      store.state.donor
        .swiftaidGivingSubscription as DonorPlanSubscription | null
  )

  const formatSubscriptionDate = (iso?: string): string | null => {
    if (!iso) return null
    const date = DateTime.fromISO(iso)
    return date.isValid ? date.toFormat('d MMMM yyyy') : null
  }

  const renewsAt = computed<string | null>(() =>
    formatSubscriptionDate(subscription.value?.renewsAt)
  )

  const endsAt = computed<string | null>(() =>
    formatSubscriptionDate(subscription.value?.endsAt)
  )

  const subscriptionPrice = computed<string | null>(() => {
    const amount = subscription.value?.price?.amount
    return typeof amount === 'number' ? formatMoneyFromPence(amount) : null
  })

  const subscriptionInterval = computed<string | null>(
    () => subscription.value?.price?.interval ?? null
  )

  // Entitlements only apply once we know the plan (or forced mock paid).
  const canDownloadStatement = computed(
    () => isPlanKnown.value && isPaid.value
  )
  const canViewStatementHistory = computed(
    () => isPlanKnown.value && isPaid.value
  )
  const canViewGivingRecord = computed(() => isPlanKnown.value && isPaid.value)
  const canAddManualDonation = computed(() => isPlanKnown.value && isPaid.value)

  /** Dev-only helper until Stripe sets plan from the API. */
  const setPlan = (next: SwiftaidGivingPlan) => {
    const allowMockUpgrade =
      import.meta.env.DEV || import.meta.env.MODE === 'mockapi'
    if (!allowMockUpgrade) return
    store.commit(SET_SWIFTAID_GIVING_PLAN, next)
    store.commit(SET_DONOR_PLAN_STATUS_KNOWN, true)
  }

  return {
    plan,
    isPlanKnown,
    isPaid,
    canDownloadStatement,
    canViewStatementHistory,
    canViewGivingRecord,
    canAddManualDonation,
    renewsAt,
    endsAt,
    subscriptionPrice,
    subscriptionInterval,
    setPlan,
  }
}
